import { OptionsDeleteIcon } from 'assets/icons/OptionsDeleteIcon'
import { CheckableList } from 'components/CheckableList/CheckableList'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { ISelectValue } from 'constants/globalTypes'
import { NotYetAdded } from 'pages/Operations/Operations.styled'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Flex } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { IShiftProgramBindingState, IShiftProgramState } from 'types/operationShiftPrograms.interface'

interface IShiftProgramBindingTableRow extends IShiftProgramBindingState {
    id: number
}

export const Bindings = ({ setStep, setState, state }: { setStep: (step: number) => void, setState?: any, state?: IShiftProgramState }) => {

    const emptyDraft: IShiftProgramBindingState = {
        taskTemplate: null,
        locations: [],
    }
    const [adding, setAdding] = useState(false)
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [draft, setDraft] = useState<IShiftProgramBindingState>(emptyDraft)

    const handleNext = () => setStep(5)
    const handleBack = () => setStep(3)

    const parseTasks = (data: any) => (data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : [])
    const parseLocations = (data: any) => (data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : [])

    const handleSelect = (value: ISelectValue, name: string) => {
        setDraft((prev) => ({ ...prev, [name]: value }))
    }

    const handleLocationsChange = (locations: ISelectValue[]) => {
        setDraft((prev) => ({ ...prev, locations }))
    }


    const handleSaveBinding = () => {
        if (!draft.taskTemplate || !draft.locations?.length) {
            toast.error('Please select a task template and locations')
            return
        }
        setState((prev: IShiftProgramState) => ({
            ...prev,
            bindings:
                editingIndex === null
                    ? [...(prev.bindings ?? []), draft]
                    : (prev.bindings ?? []).map((binding, index) => index === editingIndex ? draft : binding),
        }))
        setDraft(emptyDraft)
        setEditingIndex(null)
        setAdding(false)
    }

    const handleDeleteBinding = (index: number) => {
        setState((prev: IShiftProgramState) => ({
            ...prev,
            bindings: prev.bindings?.filter((_, i) => i !== index) ?? [],
        }))
        if (editingIndex === index) {
            setDraft(emptyDraft)
            setEditingIndex(null)
            setAdding(false)
        }
    }

    const handleRowClick = (item: IShiftProgramBindingTableRow) => {
        const index = item.id - 1
        setDraft({
            taskTemplate: item.taskTemplate,
            locations: item.locations ?? [],
        })
        setEditingIndex(index)
        setAdding(true)
    }

    const columns: IHonestTableColumn<IShiftProgramBindingTableRow>[] = [
        {
            id: 'task',
            header: 'Task',
            sticky: true,
            minWidth: '14rem',
            cell: (item) => (
                <Cell>
                    <BodyText weight='600'>{item.taskTemplate?.label ?? '-'}</BodyText>
                </Cell>
            ),
        },
        {
            id: 'locations',
            header: 'Locations',
            fill: true,
            minWidth: '20rem',
            cell: (item) => (
                <Cell>
                    <BodyText grey>{item.locations?.map((location) => location.label).join(', ')}</BodyText>
                </Cell>
            ),
        },

        {
            id: 'delete',
            header: '',
            align: 'right',
            minWidth: '3rem',
            cell: (item) => (
                <RightCell>
                    <div onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteBinding(item.id - 1)
                    }} style={{ cursor: 'pointer' }}>
                        <OptionsDeleteIcon />
                    </div>
                </RightCell>
            ),
        },
    ]

    return <>

        <Container>
            <ContainerHeader>
                <TitleText>Bindings</TitleText>
            </ContainerHeader>
            <ContainerContent>
                <BodyText style={{ marginBottom: '1rem' }} grey>Optional. Bindings tell the scheduler which task templates to instantiate at which locations when the program fires. You can add or change them later from the program detail page.</BodyText>
                <HonestTable
                    columns={columns}
                    data={(state?.bindings ?? []).map((binding, index) => ({ ...binding, id: index + 1 }))}
                    keyField='id'
                    onClick={handleRowClick}
                />
                {!adding ? <>
                    {!(state?.bindings?.length > 0) && (
                        <NotYetAdded onClick={() => setAdding(true)} >
                            <BodyText grey >No bindings yet — add one or skip and configure later.</BodyText>
                        </NotYetAdded>
                    )}
                    <Button style={{ marginTop: '1rem' }} orangeBorder onClick={() => {
                        setEditingIndex(null)
                        setDraft(emptyDraft)
                        setAdding(true)
                    }} >Add Binding</Button>
                </> : <>

                    <Container style={{ marginTop: '1rem', border: '1px solid #ff9900' }} >
                        <ContainerContent>
                            <Flex gap='1rem' >
                                <Flex column gap='1rem' width='100%' >
                                    <HonestSelectAsyncInput
                                        link='task/template/overviews'
                                        labelText='Parent Task Template'
                                        onChange={handleSelect}
                                        name='taskTemplate'
                                        placeholder='Search Task Templates'
                                        defaultValue={draft.taskTemplate?.label}
                                        parserFunction={parseTasks}
                                    />
                                    <CheckableList
                                        link='locations'
                                        parserFunction={parseLocations}
                                        searchEnabled
                                        searchPlaceholder='Search locations...'
                                        selected={draft.locations}
                                        onChange={handleLocationsChange}
                                        selectedLabelText='Selected locations'
                                        selectedPlaceholder='No locations selected'
                                        listMaxHeight='16rem'
                                    />
                                </Flex>
                            </Flex>
                            <Flex gap='1rem' mt='32'>
                                <Button onClick={() => {
                                    setAdding(false)
                                    setEditingIndex(null)
                                    setDraft(emptyDraft)
                                }}>Cancel</Button>
                                <Button orange onClick={handleSaveBinding} >{editingIndex === null ? 'Save Binding' : 'Update Binding'}</Button>
                            </Flex>
                        </ContainerContent>
                    </Container>
                </>}
            </ContainerContent>
        </Container>
        <Flex gap='1rem' mt='32' justend>
            <Button onClick={handleBack}>Back</Button>
            <Button orange onClick={handleNext} >Next</Button>
        </Flex>
    </>
}